import { PurchaseClaimedMessage, PurchaseConfirmedMessage, } from '@streamr/tv-engine';
import { executeSoon, } from '@streamr/tv-utils';
import { toUserId, toUserIdRaw, } from '@streamr/utils';
import { EventEmitter } from 'eventemitter3';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import { popProductCatalog } from '../../utils/popProductCatalog';
import { formPurchaseBackendUrl } from '../../utils/routes';
import { formPageUrl } from '../../utils/url';
import { BroadcastStateService } from '../broadcaststate/BroadcastStateService';
import { PurchaseServiceOutput } from './PurchaseServiceOutput';
const omitInternalPurchaseMetadataFields = (metadata) => {
    // TODO should we improve this so that internal and user-defined metadata can't collide,
    // e.g. by storing metadata like this:
    // {
    //    buyerUserId: ...
    //    streamId: ...
    //    custom: {
    //      buyerNickName: ...
    //      message: ...
    //      ...
    //    }
    // this method would return just the custom metadata (i.e. the metadata which the initiating plugin provided)
    // In that case we don't need this function at all
    return omit(metadata, 'buyerUserId', 'streamId');
};
const formSessionStorageKeyForProductsOfActiveBroadcast = (streamId) => `productsOfActiveBroadcast-${streamId}`;
export class PurchaseService extends EventEmitter {
    static instance;
    output;
    productsOfActiveBroadcast = [];
    streamrWrapper;
    constructor() {
        super();
        this.output = PurchaseServiceOutput.getInstance();
    }
    async init() {
        // TODO implement this more generic and type-safe way, and consider whether we should store the info
        // to somewhere else than sessionStorage. Maybe there could be config property in
        // saved broadcast (i.e. ScheduledBroadcast). Or we could have a separate "active broadcast config"
        // to which we store also other configuration attributes of the active broadcast.
        const activeBroadcast = BroadcastStateService.getInstance().getActiveBroadcast();
        if (activeBroadcast !== undefined) {
            const sessionStorageData = sessionStorage.getItem(formSessionStorageKeyForProductsOfActiveBroadcast(activeBroadcast.streamId));
            if (sessionStorageData !== null) {
                this.setProductsOfActiveBroadcast(JSON.parse(sessionStorageData), false);
            }
        }
    }
    static getInstance() {
        if (!PurchaseService.instance) {
            PurchaseService.instance = new PurchaseService();
        }
        return PurchaseService.instance;
    }
    static getNotificationInterface() {
        return PurchaseService.getInstance();
    }
    static getOutput() {
        return PurchaseServiceOutput.getReactiveOutput();
    }
    notifyProductsOfActiveBroadcastChanged(products, saveToSessionStorage) {
        this.setProductsOfActiveBroadcast(products, saveToSessionStorage);
    }
    setProductsOfActiveBroadcast(products, saveToSessionStorage) {
        this.productsOfActiveBroadcast = products;
        this.output.setProductsOfActiveBroadcast(products);
        if (saveToSessionStorage) {
            const streamId = BroadcastStateService.getInstance().getRequiredActiveBroadcast()
                .streamId;
            sessionStorage.setItem(formSessionStorageKeyForProductsOfActiveBroadcast(streamId), JSON.stringify(products));
        }
    }
    getProductsOfActiveBroadcast() {
        return this.productsOfActiveBroadcast;
    }
    sendPurchaseClaimedMessage(checkoutSessionId, merchantId) {
        if (this.streamrWrapper === undefined) {
            throw new Error('assertion failed: streamrWrapper not available');
        }
        this.streamrWrapper.publishPurchaseClaimedMessage(PurchaseClaimedMessage.create({
            checkoutSessionId,
            merchantId,
        }));
    }
    onPurchaseClaimedMessage(message, publisherId) {
        executeSoon(async () => {
            let response;
            try {
                response = await fetch(formPurchaseBackendUrl('/api/get-checkout-session'), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        id: message.checkoutSessionId,
                        merchantId: message.merchantId,
                    }),
                });
            }
            catch (err) {
                console.error('Failed to fetch checkout session', err);
                return;
            }
            if (!response.ok) {
                console.error(`Failed to fetch checkout session: HTTP status ${response.status}`);
                return;
            }
            const responseJson = await response.json();
            if (responseJson.metadata.streamId !==
                BroadcastStateService.getInstance().getActiveBroadcast()
                    ?.streamId) {
                console.log("no purchase confirmation message as the stream ID doesn't match the active broadcast");
                return;
            }
            if (responseJson.metadata.buyerUserId !== publisherId) {
                console.log('no purchase confirmation message as the claim message is not published by the buyer');
                return;
            }
            if (responseJson.paymentStatus !== 'paid') {
                console.log('no purchase confirmation message as the paymentStatus is not "paid"');
                return;
            }
            if (this.streamrWrapper === undefined) {
                throw new Error('assertion failed: streamrWrapper not available');
            }
            const pricedProduct = {
                id: responseJson.pricedProduct.id,
                merchantId: message.merchantId,
                currency: responseJson.pricedProduct.currency,
                priceUnitAmount: responseJson.pricedProduct.priceUnitAmount,
            };
            this.streamrWrapper.publishPurchaseConfirmedMessage(PurchaseConfirmedMessage.create({
                buyerUserId: toUserIdRaw(publisherId),
                productId: pricedProduct.id,
                merchantId: pricedProduct.merchantId,
                currency: pricedProduct.currency,
                priceUnitAmount: pricedProduct.priceUnitAmount,
                metadataJson: JSON.stringify(responseJson.metadata),
            }));
            this.emit('purchaseCompleted', {
                buyerUserId: publisherId,
                pricedProduct,
                metadata: omitInternalPurchaseMetadataFields(responseJson.metadata),
            });
        });
    }
    onPurchaseConfirmedMessage(message) {
        this.emit('purchaseCompleted', {
            buyerUserId: toUserId(message.buyerUserId),
            pricedProduct: {
                id: message.productId,
                merchantId: message.merchantId,
                currency: message.currency,
                priceUnitAmount: message.priceUnitAmount,
            },
            metadata: omitInternalPurchaseMetadataFields(JSON.parse(message.metadataJson)),
        });
    }
    setStreamrWrapper(streamrWrapper) {
        this.streamrWrapper = streamrWrapper;
    }
    // TODO maybe move user state management to a separate UserID service?
    getMyUserId() {
        if (this.streamrWrapper === undefined) {
            throw new Error('assertion failed: streamrWrapper not available');
        }
        return this.streamrWrapper.getMyUserId();
    }
    startPurchase(metadata) {
        const createPurchaseMetadata = (customMessage) => ({
            ...metadata,
            customMessage,
            buyerUserId: PurchaseService.getInstance().getMyUserId(),
            streamId: BroadcastStateService.getInstance().getRequiredActiveBroadcast()
                .streamId,
        });
        const createCheckoutSession = async (pricedProduct, customMessage) => {
            const response = await fetch(formPurchaseBackendUrl('/api/create-checkout-session'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    pricedProduct: pick(pricedProduct, 'id', 'merchantId', 'currency', 'priceUnitAmount'),
                    metadata: createPurchaseMetadata(customMessage),
                }),
            });
            const json = await response.json();
            return pick(json, 'id', 'clientSecret');
        };
        /**
         * @note A ticket has been created to simplify the following piece of code.
         * https://linear.app/streamr/issue/FRONT-2281/refactor-purchase-popup-flow-the-logic
         */
        executeSoon(async () => {
            const result = await popProductCatalog({
                buyerNickname: metadata.buyerNickname,
                customMessage: metadata.customMessage,
                createCheckoutSession,
            });
            if (result) {
                const broadcast = BroadcastStateService.getInstance().getRequiredActiveBroadcast();
                if (broadcast.myRole === 'viewer') {
                    this.sendPurchaseClaimedMessage(result.checkoutSessionId, result.pricedProduct.merchantId);
                }
                else if (broadcast.myRole === 'broadcaster') {
                    // Emulate sending a claim message. Currently, nodes do not receive messages
                    // that they publish themselves (see StreamrWrapper:227). Therefore, if a
                    // broadcaster sent a claim message, it would not be processed by StreamrWrapper,
                    // and StreamrTvBroadcaster’s streamrWrapper.on('purchaseClaimedMessage', ...)
                    // handler would not receive the event. If we later allow listening to
                    // self-published messages, this emulation hack should be removed.
                    const message = {
                        checkoutSessionId: result.checkoutSessionId,
                        merchantId: result.pricedProduct.merchantId,
                    };
                    this.onPurchaseClaimedMessage(message, broadcast.broadcasterUserId);
                }
                else {
                    throw new Error('Assertion failed');
                }
            }
        });
    }
    notifyLoadProductsOfPlatform() {
        this.output.setProductsOfPlatform('loading');
        executeSoon(async () => {
            const response = await fetch(formPurchaseBackendUrl('/api/get-products'), {
                method: 'POST',
            });
            const json = await response.json();
            const productsResponse = json;
            this.output.setProductsOfPlatform(productsResponse.products.map((p) => {
                // Currently there is exactly one product per merchant. Therefore the
                // we use merchant name as a product name. Also merchants support only
                // one currency, so we use that currency in all merchant's products
                // biome-ignore lint/style/noNonNullAssertion: see comment above
                const merchant = productsResponse.merchants.find((m) => m.id === p.merchantId);
                const product = {
                    id: p.id,
                    merchantId: p.merchantId,
                    name: merchant.name,
                    currency: merchant.currency,
                };
                return product;
            }));
        });
    }
    async navigateToStripeOnboardingPage(country) {
        const returnUrl = formPageUrl('/merchant-onboarding-return');
        const refreshUrl = window.location.href;
        const response = await fetch(formPurchaseBackendUrl('/api/create-merchant'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                country,
                returnUrl,
                refreshUrl,
            }),
        });
        const json = await response.json();
        const stripeUrl = json.url;
        window.location.href = stripeUrl;
    }
}
